import { Currency, MoneyModel } from '@/services/models/Money';
import { PageModel, PageType, type IPage } from '@/services/models/Page';
import { ProductModel } from '@/services/models/Product';
import type { Nullable, Result } from '@/type-utils';
import { unique } from '@/utils/array-utils';
import { withPromiseResolvers } from '@/utils/async-utils';
import { AxiosInstance } from 'axios';
import { createAPIClient } from '@/utils/network-utils/createAPIClient';
import { RecommendationContext } from '@/configs/recommendations';
import { InvalidArgumentError, InvalidStateError } from '@/utils/errors';
import type { IDYEngagementConfiguration } from '@/services/serverless/integrations/DYService/schema/report/IDYEngagementConfiguration';
import { isNullish } from '@/utils/null-utils';
import { sha256Hex } from '@/utils/hash-utils';
import Service from '../../Service';
import {
  DYPageTypeMap,
  DYService,
  IDYChoiceConfiguration,
  IDYEventConfiguration,
  IDYEventProduct,
  IDYPageViewConfiguration,
  IDYSelector
} from '../../serverless/integrations/DYService';

import {
  IDYChoice,
  type IDYExperienceChoice,
  type IDYRecommendationsChoice
} from '../../serverless/integrations/DYService/schema/response/IDYChoice';
import siteCached from '../../utils/siteCached';
import CartService from '../CartService';
import CookieService from '../CookieService';
import ConfigurationService, { Config } from '../ConfigurationService';
import { EnvironmentService } from '../EnvironmentService';
import i18nService from '../I18NService';
import {
  EventType,
  IInteractionPageDetails,
  InteractionDetails,
  type InteractionEngagementDetails
} from '../UserInteractionService/IInteractionDetails';
import {
  PersonalizationData,
  type IRecommendationsChoiceData
} from './PersonalizationData';
import { PersonalizationDataType } from './PersonalizationDataType';
import PersonalizationServiceMock from './PersonalizationServiceMock';
import {
  DecisionIDOf,
  ExperienceID,
  IDecision,
  decisionPropsSchemas,
  experienceToDecisionsMap,
  type Decision,
  type DecisionsFor,
  type IExperienceToDecisionsMapping
} from './campaigns';
import { PersonalizationConfigurationError } from './errors/PersonalizationConfigurationError';
import type { IRecommendationsData } from '../RecommendationService';

/** The choice data coming back from DY. */
export interface IChoiceData {
  /** The choices that come back from DY, in DY shape. */
  choices: Array<IDYChoice>;
}

/**
 * Handles the swapping of different experiences based on a personalization
 * integration service (Dynamic Yield Service).
 */
export class PersonalizationService extends Service {
  /**
   * The Dynamic Yield config.
   * @returns A `Config<'dynamicYield'>`.
   */
  @siteCached
  private get dyConfig(): Config<'dynamicYield'> {
    return ConfigurationService.getConfig('dynamicYield');
  }

  private client: AxiosInstance;

  /**
   * Promise that resolves once the first client-side DY Choose call completes.
   * Establishes identity cookies in the browser via Set-Cookie response
   * headers. Null when not in use.
   *
   * Per DY docs, sequential calls are required for new users.
   *
   * Once the first call resolves, subsequent calls proceed in parallel since
   * the browser automatically includes the established cookies.
   *
   * WARNING (TODO): This gate operates within a single tab. Concurrent tabs
   * each establish identity independently. This should be expanded to support
   * concurrent tabs in the future.
   *
   * @see https://dy.dev/docs/using-experience-apis-and-script-together
   */
  private clientIdentityGate: Promise<void> | null = null;

  /** Constructs a new `PersonalizationService`. */
  public constructor() {
    super();

    this.client = createAPIClient({ baseURL: '/api/personalization' });
  }

  /**
   * Ensures DY identity cookies are established before making a client-side
   * Choose call. The first caller proceeds immediately; concurrent callers
   * await the same promise. Once the first call completes (browser receives
   * `Set-Cookie` headers), all subsequent calls proceed in parallel since
   * the browser automatically includes the cookies.
   *
   * Server-side calls bypass this gate — identity is handled by
   * `@requestCached` on `DYService.chooseNoVariation()`.
   *
   * TODO: Consider moving this gate to a client-only subclass or to axios
   * middleware to preserve the isomorphic contract — see follow-ups in MR !2663.
   *
   * @param fn - The async function that makes the client-side DY API call.
   * @returns The result of the API call.
   * @see https://dy.dev/docs/using-experience-apis-and-script-together
   */
  private async withClientIdentityGate<T>(fn: () => Promise<T>): Promise<T> {
    // Server-side: identity handled by @asyncDeduped in DYService.
    if ((typeof window === "undefined")) {
      return fn();
    }

    // If DY cookies already exist in the browser, skip the gate.
    if (CookieService.has('_dyid_server')) {
      return fn();
    }

    // If a call is already in-flight establishing identity, wait for it
    // then proceed — cookies will be set by the time we make our call.
    if (this.clientIdentityGate) {
      await this.clientIdentityGate;
      return fn();
    }

    // First caller for a new user — establish identity.
    // Other callers will await this same promise.
    const { promise, resolve } = withPromiseResolvers<void>();
    this.clientIdentityGate = promise;

    try {
      return await fn();
    } finally {
      resolve();
      this.clientIdentityGate = null;
    }
  }

  /**
   * The preview parameter for the DY API. If present in the URL, the DY API will return the preview
   * version of the campaign.
   * @returns The preview parameter.
   */
  private get previewParam(): string {
    return this.dyConfig.getSetting('previewParam').value;
  }

  /**
   * Converts our personalization data into the format needed in the `DYService`.
   * @param personalizationData - Contains a page and the ids for the experiences we want to retrieve.
   * @returns The body object used to request the choice from the DYService.
   */
  private getDYConfigurationObject(
    personalizationData: PersonalizationData
  ): IDYChoiceConfiguration | IDYPageViewConfiguration {
    const { type } = personalizationData;
    let { page } = personalizationData;
    const { language, country } = i18nService.currentLocale;

    page ??= { pageType: PageType.Unknown, url: EnvironmentService.url.origin };

    const pageModel = PageModel.from(page);
    const location = pageModel.canonicalURL
      ? pageModel.canonicalURL.toString()
      : EnvironmentService.url.origin;

    let selector: Nullable<IDYSelector> = null;
    if (type === PersonalizationDataType.Choice) {
      const { ids } = personalizationData;

      if (!isNullish(ids)) {
        selector = { names: ids };
      }

      const { url } = EnvironmentService;
      if (url.searchParams.has(this.previewParam)) {
        const previewId = url.searchParams.get(this.previewParam) as string;
        if (selector) {
          selector = { ...selector, preview: { ids: [previewId] } };
        }
      }
    }

    let data: ReadonlyArray<string> = [];
    if (type === PersonalizationDataType.Category) {
      const { categories } = personalizationData;
      data = categories;
    } else if (type === PersonalizationDataType.Product) {
      const { products } = personalizationData;
      data = products;
    } else if (
      !isNullish(personalizationData.contextData) &&
      personalizationData.contextData?.length > 0
    ) {
      // For Choice types, use contextData if provided. This allows callers to
      // pass page-type-specific data (e.g., product SKU for PDP pages) that
      // DY requires in the page context for /choose calls.
      data = personalizationData.contextData;
    }

    let dyConfiguration: IDYPageViewConfiguration = {
      context: {
        page: {
          type: DYPageTypeMap[pageModel.pageType],
          location,
          locale: `${language}_${country}`,
          data
        }
      }
    };

    if (selector) {
      dyConfiguration = {
        ...dyConfiguration,
        selector
      } as IDYChoiceConfiguration;
    }

    /**
     * This section is for adding analytics data off the window
     * object or other configuration that can only be assembled
     * on the client-side.
     */
    if ((typeof window !== "undefined")) {
      const device = { userAgent: window.navigator.userAgent };

      dyConfiguration.context = { ...dyConfiguration.context, ...device };
    }

    return dyConfiguration;
  }

  /**
   * Gets a choice using a recommendations context as the id.
   * @param context - A recommendations context to be used as the choice id.
   * @param page - The current page for the request.
   * @param contextData - Optional page-type-specific data for the DY context
   * (e.g., the product SKU for PRODUCT pages).
   * @returns The choices from the `DYService`.
   */
  private async getRecommendationsChoices(
    context: RecommendationContext,
    page: IPage,
    contextData?: ReadonlyArray<string>
  ): Promise<Array<IDYChoice>> {
    const dyConfiguration = this.getDYConfigurationObject({
      type: PersonalizationDataType.Choice,
      ids: [context],
      page,
      contextData
    } as IRecommendationsChoiceData) as IDYChoiceConfiguration;

    if ((typeof window === "undefined")) {
      const data = await DYService.chooseVariations(dyConfiguration);

      return data.choices ?? [];
    }

    return this.withClientIdentityGate(async () => {
      const { data } = await this.client.post<IChoiceData>(
        `/choices`,
        dyConfiguration
      );

      return data.choices ?? [];
    });
  }

  /**
   * Gets the choices from the `DYService`, if on the Server it directly
   * asks the service. If on the client it makes the request to the api
   * route.
   * @param experienceNames - The names of the experiences in an array.
   * @param page - The current page being assembled or that the user is on.
   * @returns The choices from the `DYService`.
   */
  private async getChoices<const EIDs extends ReadonlyArray<ExperienceID>>(
    experienceNames: EIDs,
    page?: Nullable<IPage>
  ): Promise<Array<IDYExperienceChoice>> {
    const dyConfiguration = this.getDYConfigurationObject({
      type: PersonalizationDataType.Choice,
      ids: experienceNames,
      page
    }) as IDYChoiceConfiguration;

    if ((typeof window === "undefined")) {
      const { choices } = await DYService.chooseVariations(dyConfiguration);

      return choices as Array<IDYExperienceChoice>;
    }

    return this.withClientIdentityGate(async () => {
      const { data } = await this.client.post<IChoiceData>(
        `/choices`,
        dyConfiguration
      );

      return (data.choices as Array<IDYExperienceChoice>) ?? [];
    });
  }

  /**
   * Gets the recommendations for the current user.
   * @param recommendationContext - The context to get the recommendations for.
   * @param page - The current page that the user is on.
   * @param contextData - Optional page-type-specific data for the DY context
   * (e.g., the product SKU for PRODUCT pages).
   * @returns The choices from the `DYService`.
   */
  public async getRecommendationsForCurrentUser(
    recommendationContext: RecommendationContext,
    page: IPage,
    contextData?: ReadonlyArray<string>
  ): Promise<IRecommendationsData> {
    const choices = await this.getRecommendationsChoices(
      recommendationContext,
      page,
      contextData
    );
    const choice = choices.length > 0 ? choices[0] : null;

    let title = '';
    let parentTitle = '';
    let productList: Array<string> = [];
    let slotIds: Array<string> = [];

    if (choice) {
      const { variations } = choice as IDYRecommendationsChoice;
      if (variations.length > 0) {
        // TODO: Handle multiple variations or multi touch campaigns. DECA-5678
        const [variation] = variations;
        const payload = variation.payload.data;
        title = payload.custom.Title;
        parentTitle = payload.custom.ParentTitle;
        productList = payload.slots.map((slot) => slot.sku);
        slotIds = payload.slots.map((slot) => slot.slotId);
      }
    }

    return { title, productList, parentTitle, slotIds };
  }

  /**
   * Gets a single experience .
   * @param experienceName - The experience name to be returned.
   * @param page - The current page being assembled or that the user is on.
   * @returns The decisions, a list of experience names with their chosen decision id.
   * @throws If the experience doesn't exist.
   */
  public async getExperienceForCurrentUser<const T extends ExperienceID>(
    experienceName: T,
    page?: Nullable<IPage>
  ): Promise<IDecision<T, DecisionIDOf<T>>> {
    const decisionsRecord = await this.getExperiencesForCurrentUser(
      [experienceName],
      page
    );

    const [decision, error] = decisionsRecord[experienceName];

    if (error !== null) throw error;

    return decision;
  }

  /**
   * Gets all the decisions for all requested experience names.
   * @param experienceNames - A non-empty array of experience names to retrieve decisions for.
   * @param page - The current page being assembled or that the user is on.
   * @returns A 2-tuple containing the decisions and any errors that occurred.
   *
   * @todo This method enables a potential optimization because DY allows us to
   * request variations for multiple selectors at the same time. As a result,
   * it would be more efficient to first group all the selectors on the page,
   * and then make one request for all of them.
   */
  public async getExperiencesForCurrentUser<
    const EIDs extends readonly [ExperienceID, ...Array<ExperienceID>]
  >(
    experienceNames: EIDs,
    page?: Nullable<IPage>
  ): Promise<DecisionsFor<EIDs>> {
    // Dedupe the experience names.
    const uniqueExperienceNames = unique(experienceNames);

    const choices = await this.getChoices(uniqueExperienceNames, page);

    // Initialize the result object.
    // Note: This type is not as strict as it could be, i.e.
    // DecisionsFor<Array<ExperienceID>>, however we had to simplify it to
    // prevent Out-Of-Memory errors during type-checking.
    const result = {} as Record<ExperienceID, Result<Decision, Error>>;

    for (const experienceName of uniqueExperienceNames) {
      // Check if the experience name is known.
      if (isNullish(experienceToDecisionsMap[experienceName])) {
        const error = new PersonalizationConfigurationError(
          `"${experienceName}" is not a known experience name. Please check the \`experienceToDecisionsMap\`.`
        );

        result[experienceName] = [null, error];
        continue;
      }

      // Check if there are any choices for the experience.
      const experienceChoice = choices.find(
        (choice) => choice.name === experienceName
      );

      if (!experienceChoice) {
        const error = new PersonalizationConfigurationError(
          `No choices found for experience "${experienceName}".`
        );

        result[experienceName] = [null, error];
        continue;
      }

      const errors: Array<Error> = [];

      const experienceID =
        experienceChoice.name as keyof IExperienceToDecisionsMapping;
      const engagementID = experienceChoice.decisionId;
      const firstVariation = experienceChoice.variations[0];
      const { decisionID, props } = firstVariation.payload.data;

      if (!decisionID) {
        errors.push(
          new PersonalizationConfigurationError(
            `Variation ${firstVariation.id} does not have a \`decisionID\` in the payload data. Make sure the capitalization is correct.`
          )
        );
      } else {
        // Check if the decision is valid for the experience.
        const validDecisionsForExperience = experienceToDecisionsMap[
          experienceID
        ] as Array<string>;

        if (!validDecisionsForExperience.includes(decisionID)) {
          errors.push(
            new PersonalizationConfigurationError(
              `"${decisionID}" is not a valid decision for experience "${experienceID}". Expected one of: ${validDecisionsForExperience.join(
                ', '
              )}`
            )
          );
        }

        // Check if the props match the expected schema.
        const decisionPropsSchema =
          decisionPropsSchemas[decisionID as DecisionIDOf<ExperienceID>];
        if (isNullish(decisionPropsSchema)) {
          errors.push(
            new PersonalizationConfigurationError(
              `No props schema found for decision "${decisionID}".`
            )
          );
        } else if (!decisionPropsSchema.safeParse(props).success) {
          errors.push(
            new PersonalizationConfigurationError(
              `Props for decision "${decisionID}" do not match the expected schema.`
            )
          );
        }
      }

      // Do not add the decision if it contains any errors.
      // Instead add it to the aggregate errors.
      if (errors.length > 0) {
        result[experienceID] = [
          null,
          new AggregateError(
            errors,
            `Encountered errors for experience "${experienceID}".`
          )
        ];
      } else {
        const decision = {
          experienceID,
          engagementID,
          decisionID: decisionID as DecisionIDOf<typeof experienceID>,
          props
        };

        result[experienceID] = [decision, null];
      }
    }

    return result as DecisionsFor<EIDs>;
  }

  /**
   * Sets the recommendation context for the user. This adds data
   * to the DY widget and sets the page context.
   * @param pageView - The page view data to be registered.
   * @throws InvalidArgumentError - If the page isn't given.
   * @throws InvalidStateError - If the DY object isn't available.
   */
  private setRecommendationContext(pageView: IDYPageViewConfiguration): void {
    if (!(typeof window === "undefined") && 'DY' in window) {
      const { page } = pageView.context;

      if (!page) {
        throw new InvalidArgumentError(
          'The page context must be set before sending a recommendation context.'
        );
      }

      const context = {
        type: page.type,
        lng: page.locale,
        data: page.data as Array<string>
      };

      if (window?.DY === undefined) {
        throw new InvalidStateError(
          'DY is not available on the window object.'
        );
      }

      window.DY.recommendationContext = context;
    }
  }

  /**
   * Sends an engagement event using the interaction details to construct a DY engagement
   * with a variation or recommendation served from an API campaign.
   * @param interactionDetails - The engagement interaction event.
   */
  public async sendEngagement(
    interactionDetails: InteractionEngagementDetails
  ): Promise<void> {
    const isDYEnabled = this.dyConfig.getSetting('isDYEnabled').value;

    if (isDYEnabled) {
      const dyConfiguration: IDYEngagementConfiguration = {
        context: {},
        engagements: []
      };

      if (interactionDetails.action === EventType.EngagementVariationClick) {
        dyConfiguration.engagements.push({
          type: 'CLICK',
          decisionId: interactionDetails.decisionId,
          variations: interactionDetails.variations
        });
      }
      if (
        interactionDetails.action === EventType.EngagementRecommendationClick
      ) {
        dyConfiguration.engagements.push({
          type: 'SLOT_CLICK',
          slotId: interactionDetails.slotId
        });
      }
      if ((typeof window !== "undefined")) {
        const device = { userAgent: window.navigator.userAgent };

        dyConfiguration.context = { ...dyConfiguration.context, ...device };
      }
      if ((typeof window === "undefined")) {
        await DYService.sendReport('engagement', dyConfiguration);
      } else {
        /**
         * The `dyID` is required ahead of time for analytics events, but for choices one will be
         * assigned to the user if they do not have one.
         */
        await this.client.post(`/engagement`, dyConfiguration);
      }
    }
  }

  /**
   * Sets the page context for personalization. This sends data to the {@link DYService}. Which uses that
   * data to set a context for the given user. This context is essentially a page view event.
   * @param interactionDetails - The page interaction event.
   * @see https://support.dynamicyield.com/hc/en-us/articles/360022955254#h_01F46DQ0R7HWBXVYHWAG642CYY
   */
  public async sendPageContext(
    interactionDetails: IInteractionPageDetails
  ): Promise<void> {
    const isDYEnabled = this.dyConfig.getSetting('isDYEnabled').value;

    if (isDYEnabled) {
      const { page, products, lines } = interactionDetails;

      let dyConfiguration: IDYPageViewConfiguration;

      switch (page.pageType) {
        case PageType.Category:
        case PageType.Search: {
          const categories = page.url
            .split('/')
            .filter((crumb) => crumb !== '' && crumb !== 'c' && crumb !== 's');

          dyConfiguration = this.getDYConfigurationObject({
            type: PersonalizationDataType.Category,
            categories,
            page
          }) as IDYPageViewConfiguration;
          break;
        }

        case PageType.Cart: {
          const upcs = lines?.map((line) => line.upc) ?? [];

          dyConfiguration = this.getDYConfigurationObject({
            type: PersonalizationDataType.Product,
            products: upcs,
            page
          }) as IDYPageViewConfiguration;
          break;
        }

        default: {
          const upcs = products?.map((product) => {
            const productModel = ProductModel.from(product);
            const upc = product.upc ?? productModel.fallbackUPC;
            return upc;
          });

          dyConfiguration = this.getDYConfigurationObject({
            type: PersonalizationDataType.Product,
            products: upcs ?? [],
            page
          }) as IDYPageViewConfiguration;
          break;
        }
      }

      if ((typeof window === "undefined")) {
        await DYService.sendReport('pageview', dyConfiguration);
      } else {
        // Only do the client side report if on the client side.
        this.setRecommendationContext(dyConfiguration);
      }
    }
  }

  /**
   * Sends an event using the interaction details to construct an dy event.
   * This does not return anything, but could throw an error, which should be caught in
   * most cases.
   * @param interactionDetails - The interaction details used to construct the dy event.
   * Depending on the {@link EventType} different data will be sent.
   */
  public async sendEvent(
    interactionDetails: InteractionDetails
  ): Promise<void> {
    const isDYEnabled = this.dyConfig.getSetting('isDYEnabled').value;

    if (isDYEnabled) {
      const dyConfiguration: IDYEventConfiguration = {
        context: {},
        events: []
      };

      if (interactionDetails.action === EventType.ProductView) {
        dyConfiguration.events.push({
          name: 'Product Display Page View',
          properties: { dyType: 'pdp-view-v1' }
        });
      }

      // Adding product add event data.
      if (interactionDetails.action === EventType.ProductAdd) {
        /** In a ProductAdd event, cart data should be assembled from the users current cart. */
        const cart = await CartService.getCartFromSession();
        // We don't need to call revalidate here because we
        // only care about the cart items, not the totals.

        /**
         * If there is a cart, it will be used to get the skus, quantities, and total prices
         * of all items in the cart. This information will be a part of the event data.
         */
        let cartItems: Array<IDYEventProduct> = [];
        if (cart) {
          cartItems = cart.items.map((item): IDYEventProduct => {
            return {
              productId: item.upc,
              quantity: item.quantity,
              // DY's `itemPrice` is the per-unit price, not the line total.
              // @see https://dy.dev/docs/add-to-cart — "itemPrice: The price of the item added"
              // eslint-disable-next-line local-rules/warn-against-moneymodel-asunsafenumber -- DY expects a number here.
              itemPrice: MoneyModel.asUnsafeNumber(item.unitPrice.price)
            };
          });
        }
        const {
          product: { price, upc }
        } = interactionDetails;

        // Use current price for events
        const priceToUse = price?.price;

        dyConfiguration.events.push({
          name: 'Add to Cart',
          properties: {
            dyType: 'add-to-cart-v1',
            value: priceToUse
              ? // eslint-disable-next-line local-rules/warn-against-moneymodel-asunsafenumber -- DY expects a number here.
                MoneyModel.asUnsafeNumber(priceToUse)
              : 0,
            currency: priceToUse?.currency ?? Currency.USD,
            // Non-null asserted because the product should always be complete on cart.
            productId: upc!,
            quantity: 1,
            cart: cartItems
          }
        });
      }

      // Purchase order data.
      if (interactionDetails.action === EventType.OrderSuccess) {
        const { orderData, cartData } = interactionDetails;

        /**
         * In a purchase event the current users cart will already be empty by the time this
         * event is fired. Instead the cart data will be pulled from whatever cart was used to
         * populate the order data for the place order call.
         */
        const cartItems = cartData.items.map((item): IDYEventProduct => {
          return {
            // Non-null asserted because the product should always be complete on place order.
            productId: item.upc,
            quantity: item.quantity,
            // DY's `itemPrice` is the per-unit price, not the line total.
            // @see https://dy.dev/docs/add-to-cart — "itemPrice: The price of the item added"
            itemPrice: !isNullish(item.unitPrice.price)
              ? // eslint-disable-next-line local-rules/warn-against-moneymodel-asunsafenumber -- DY expects a number here.
                MoneyModel.asUnsafeNumber(item.unitPrice.price)
              : 0
          };
        });

        dyConfiguration.events.push({
          name: 'Purchase',
          properties: {
            dyType: 'purchase-v1',
            // eslint-disable-next-line local-rules/warn-against-moneymodel-asunsafenumber -- DY expects a number here.
            value: MoneyModel.asUnsafeNumber(orderData.totals.total),
            currency: orderData.totals.total.currency,
            uniqueTransactionId: orderData.orderID?.value ?? '',
            cart: cartItems
          }
        });
      }

      if (interactionDetails.action === EventType.Scroll) {
        dyConfiguration.events.push({
          name: 'Scroll',
          properties: {
            dyType: 'scroll-v1',
            page: interactionDetails.page,
            scrollPositionPercentage:
              interactionDetails.scrollPositionPercentage,
            device: interactionDetails.device
          }
        });
      }

      // Login event - sent when a user successfully logs in.
      if (
        interactionDetails.action === EventType.LoginSuccess &&
        'customer_email' in interactionDetails
      ) {
        const cuid = await sha256Hex(interactionDetails.customer_email);
        dyConfiguration.events.push({
          name: 'Login',
          properties: { dyType: 'login-v1', cuid, cuidType: 'he' }
        });
      }

      // Signup event - sent when a user successfully signs up.
      if (
        interactionDetails.action === EventType.SignupSuccess &&
        'customer_email' in interactionDetails
      ) {
        const cuid = await sha256Hex(interactionDetails.customer_email);
        dyConfiguration.events.push({
          name: 'Signup',
          properties: { dyType: 'signup-v1', cuid, cuidType: 'he' }
        });
      }

      if ((typeof window !== "undefined")) {
        const device = { userAgent: window.navigator.userAgent };

        dyConfiguration.context = { ...dyConfiguration.context, ...device };
      }
      if ((typeof window === "undefined")) {
        await DYService.sendReport('event', dyConfiguration);
      } else {
        /**
         * The `dyID` is required ahead of time for analytics events, but for choices one will be
         * assigned to the user if they do not have one.
         */
        await this.client.post(`/event`, dyConfiguration);
      }
    }
  }
}

export default PersonalizationService.withMock(
  new PersonalizationServiceMock(PersonalizationService)
);
